import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PaymentMethodSlug } from 'config/types/order.type';
import { StoreCoupon, StoreOrder } from 'store/models/order.model';

const initialStateCoupon: StoreCoupon = {
  isFetching: false,
  isValid: false,
  message: '',
  code: '',
};

const initialState: StoreOrder = {
  taxes: [],
  subTotal: 0,
  subTotalFormatted: '',
  total: 0,
  totalFormatted: '',
  isFetching: false,
  deliveryAllowed: false,
  pickupAllowed: false,
  couponsAllowed: false,
  preOrderAllowed: false,
  paymentMethods: [],
  coupon: initialStateCoupon,
  isDelivery: false,
  isPickup: false,
  discounts: [],
  entryCode: null,
  idempotencyKey: null,
  selectedPaymentMethod: null,
  buyerNotes: '',
};

type CheckoutDataPayload = Pick<
  StoreOrder,
  | 'taxes'
  | 'subTotal'
  | 'subTotalFormatted'
  | 'total'
  | 'totalFormatted'
  | 'deliveryAllowed'
  | 'pickupAllowed'
  | 'couponsAllowed'
  | 'preOrderAllowed'
  | 'paymentMethods'
>;

type VerifyCouponResponsePayload = Pick<StoreCoupon, 'isValid' | 'message'>;

type VerifyCouponSuccessResponse = Pick<StoreOrder, 'taxes' | 'subTotal' | 'subTotalFormatted' | 'total' | 'totalFormatted' | 'discounts'>;

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    refreshBuyerNotes: (state, action: PayloadAction<string>) => {
      state.buyerNotes = action.payload;
      return state;
    },
    refreshSelectedPaymentMethod: (state, action: PayloadAction<PaymentMethodSlug | null>) => {
      state.selectedPaymentMethod = action.payload;
      return state;
    },
    refreshEntryCode: (state, action: PayloadAction<string | null>) => {
      state.entryCode = action.payload;
      return state;
    },
    refreshIdempotencyKey: (state, action: PayloadAction<string | null>) => {
      state.idempotencyKey = action.payload;
      return state;
    },
    resetIdempotencyKey: (state) => {
      state.idempotencyKey = null;
      return state;
    },
    refreshToCleanDiscounts: (state) => {
      state.discounts = [];
      return state;
    },
    refreshAppliedCouponData: (state, action: PayloadAction<VerifyCouponSuccessResponse>) => {
      state.taxes = action.payload.taxes;
      state.subTotal = action.payload.subTotal;
      state.subTotalFormatted = action.payload.subTotalFormatted;
      state.total = action.payload.total;
      state.totalFormatted = action.payload.totalFormatted;
      state.discounts = action.payload.discounts;
      return state;
    },
    refreshIsDelivery: (state, action: PayloadAction<boolean>) => {
      state.isDelivery = action.payload;
      state.isPickup = !action.payload;
      return state;
    },
    refreshDeliveryType: (state, action: PayloadAction<{ isDelivery: boolean; isPickup: boolean }>) => {
      state.isDelivery = action.payload.isDelivery;
      state.isPickup = action.payload.isPickup;
      return state;
    },
    fetchingRefresh: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
      return state;
    },
    refreshCheckoutData: (state, action: PayloadAction<CheckoutDataPayload>) => {
      state.taxes = action.payload.taxes;
      state.subTotal = action.payload.subTotal;
      state.subTotalFormatted = action.payload.subTotalFormatted;
      state.total = action.payload.total;
      state.totalFormatted = action.payload.totalFormatted;
      state.deliveryAllowed = action.payload.deliveryAllowed;
      state.pickupAllowed = action.payload.pickupAllowed;
      state.couponsAllowed = action.payload.couponsAllowed;
      state.preOrderAllowed = action.payload.preOrderAllowed;
      state.paymentMethods = action.payload.paymentMethods;
      return state;
    },
    refreshCouponFetching: (state, action: PayloadAction<boolean>) => {
      state.coupon.isFetching = action.payload;
      return state;
    },
    refreshCouponCode: (state, action: PayloadAction<string>) => {
      state.coupon.code = action.payload;
      return state;
    },
    refreshVerifyCouponResponse: (state, action: PayloadAction<VerifyCouponResponsePayload>) => {
      state.coupon.isValid = action.payload.isValid;
      state.coupon.message = action.payload.message;
      return state;
    },
    resetOrderStore: () => initialState,
  },
});

export const OrderSlice = orderSlice.actions;
export default orderSlice.reducer;
